import { array } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DSSetup, Service } from 'webapp-types';

import joinClasses from 'webapp/helpers/join-classes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import ToolTipComponent from 'webapp/components/tool-tip';
import VerificationBadgeComponent from 'webapp/components/verification/badge';

import type { Metric } from 'webapp/models-custom';

interface Args {
	metricSetupData: DSSetup.MetricSetupData;
	onMetricSelect: () => void;
	disabledTooltipText?: string;
	emptyGuidanceText?: string;
}

export default class MetricDetailsMetricSelectorComponent extends Component<Args> {
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;

	get metric(): Metric | null {
		const { key, spaceAccessId } = this.args.metricSetupData;
		if (!key) {
			return null;
		}
		return this.mmMetricsService.getMetricForKeyAndSpaceAccessId(key, spaceAccessId) ?? null;
	}

	get isMetricSelectorDisabled(): boolean {
		return !!this.args.disabledTooltipText;
	}

	get isReadOnly(): boolean {
		return this.metricDetailsService.isMobileMode;
	}

	get showChevron(): boolean {
		return !this.isMetricSelectorDisabled && !this.isReadOnly;
	}

	get showEmptyGuidance(): boolean {
		return !this.metric && !!this.args.emptyGuidanceText && !this.isReadOnly;
	}

	<template>
		<div class="metric-select-new">
			<div
				class={{joinClasses
					(array
						"metric-selector"
						(unless this.metric "empty")
						(if this.isMetricSelectorDisabled "disabled")
						(if this.isReadOnly "read-only")
					)
				}}
				role="button"
				{{on "click" @onMetricSelect}}
			>
				<div class="metric-info">
					{{#if this.metric}}
						{{#if this.metric.sourceAvatar}}
							<img
								alt="Source avatar"
								class="datasource-icon"
								src={{this.metric.sourceAvatar}}
							/>
						{{/if}}
						<span class="datasource-name">
							{{this.metric.sourceName}}
						</span>&nbsp;/&nbsp;
						<span class="metric-name">{{this.metric.name}}</span>
						<VerificationBadgeComponent
							@verification={{this.metric.verificationInfo}}
							@entityLabel="metric"
							@size="sm"
						/>
					{{else}}
						<span class="placeholder-text">Select metric</span>
					{{/if}}
				</div>
				{{#if this.showChevron}}
					<CommonUiIconComponent @name="ChevronDown" @size="sm" />
				{{/if}}
			</div>
			{{#if @disabledTooltipText}}
				<ToolTipComponent @text={{@disabledTooltipText}} />
			{{/if}}
		</div>
		{{#if this.showEmptyGuidance}}
			<div class="metric-details__empty-guidance">
				<CommonUiIconComponent @name="ArrowLeftUp" @size="xl" />
				<span class="metric-details__empty-guidance-text">{{@emptyGuidanceText}}</span>
			</div>
		{{/if}}
	</template>
}
